import { useEffect, useMemo, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  CalendarIcon, Plus, Trash2, GitCompare, Loader2,
  LineChart as LineIcon, BarChart3 as BarIcon, AreaChart as AreaIcon, PieChart as PieIcon,
  ChevronDown, ChevronUp, Copy, Eye, EyeOff,
} from "lucide-react";
import {
  ComposedChart, Line, Bar, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { api, formatCurrency } from "@/lib/api";
import { toast } from "sonner";
import { SERIES_COLORS, colorFor } from "./chartConstants";

const METRIC_OPTIONS = [
  { value: "ca", label: "CA (chiffre d'affaires)" },
  { value: "orders_amount", label: "Commandes (montant)" },
  { value: "orders_count", label: "Commandes (nombre)" },
  { value: "stock", label: "Stock de commandes" },
  { value: "objective", label: "Objectif annuel" },
];

const CHART_TYPES = [
  { value: "line", label: "Courbe", Icon: LineIcon },
  { value: "bar", label: "Barres", Icon: BarIcon },
  { value: "area", label: "Aires", Icon: AreaIcon },
  { value: "pie", label: "Camembert", Icon: PieIcon },
];

const isCountMetric = (m) => m === "orders_count";

const today = () => new Date();
const yearStart = (y) => new Date(y, 0, 1);
const yearEnd = (y) => new Date(y, 11, 31);

const defaultSeries = (index = 0) => {
  const y = today().getFullYear() - index;
  return {
    id: `s_${Date.now()}_${index}`,
    label: `${y}`,
    metric: "ca",
    start_date: yearStart(y),
    end_date: index === 0 ? today() : yearEnd(y),
    cumulative: false,
    chart_type: "line",
    color: colorFor(index),
    order_type_ids: [],
    seller_ids: [],
  };
};

const toggleId = (list, id) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

// ----- Tooltip (outside parent to avoid unstable component) -----
const CompareTooltip = ({ active, payload, computed }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white p-3 border rounded shadow-lg text-xs space-y-1">
      <p className="font-semibold mb-1">{payload[0]?.payload?.__label}</p>
      {payload.map((entry, i) => {
        const sId = entry.dataKey;
        const s = computed.find((x) => x.id === sId);
        const period = entry.payload?.[`${sId}__period`];
        const count = entry.payload?.[`${sId}__count`];
        const isCount = s && isCountMetric(s.metric);
        return (
          <p key={i} style={{ color: entry.color }}>
            <span className="font-medium">{s?.label || sId}</span>
            {period && <span className="text-muted-foreground"> ({period})</span>}
            {" : "}
            {isCount ? `${entry.value} cmd.` : formatCurrency(entry.value || 0)}
            {!isCount && count > 0 && (
              <span className="text-muted-foreground"> · {count} chantier{count > 1 ? "s" : ""}</span>
            )}
          </p>
        );
      })}
    </div>
  );
};

// ----- Series row (outside parent) -----
const SeriesCard = ({
  s, idx, totalCount, orderTypes, sellersList,
  open, onToggleOpen, onUpdate, onDuplicate, onRemove,
}) => {
  const selectedTypeNames = orderTypes.filter((t) => s.order_type_ids.includes(t.id)).map((t) => t.name);
  const selectedSellerNames = sellersList.filter((sl) => s.seller_ids.includes(sl.id)).map((sl) => sl.name);
  const ChartIcon = CHART_TYPES.find((c) => c.value === s.chart_type)?.Icon || LineIcon;
  const startDate = s.start_date instanceof Date ? s.start_date : new Date(s.start_date);
  const endDate = s.end_date instanceof Date ? s.end_date : new Date(s.end_date);

  return (
    <Card className="border-l-4" style={{ borderLeftColor: s.color }} data-testid={`series-card-${idx}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: s.color }} />
          <Input
            value={s.label}
            onChange={(e) => onUpdate(idx, { label: e.target.value })}
            className="h-8 text-sm font-medium flex-1"
            placeholder="Nom de la série"
            data-testid={`series-label-${idx}`}
          />
          <ChartIcon className="h-4 w-4 text-muted-foreground" />
          <Button
            variant="ghost" size="icon" className="h-7 w-7"
            onClick={() => onToggleOpen(s.id)}
            title={open ? "Réduire" : "Déplier"}
            data-testid={`series-collapse-${idx}`}
          >
            {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </Button>
          <Button
            variant="ghost" size="icon" className="h-7 w-7"
            onClick={() => onDuplicate(idx)}
            title="Dupliquer"
            data-testid={`series-duplicate-${idx}`}
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
          {totalCount > 1 && (
            <Button
              variant="ghost" size="icon" className="h-7 w-7"
              onClick={() => onRemove(idx)}
              title="Supprimer"
              data-testid={`series-remove-${idx}`}
            >
              <Trash2 className="h-3.5 w-3.5 text-destructive" />
            </Button>
          )}
        </div>
      </CardHeader>
      {open && (
        <CardContent className="space-y-3 pt-0">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Métrique</Label>
              <Select value={s.metric} onValueChange={(v) => onUpdate(idx, { metric: v })}>
                <SelectTrigger className="h-8 text-xs" data-testid={`series-metric-${idx}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {METRIC_OPTIONS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Type de graphique</Label>
              <Select value={s.chart_type} onValueChange={(v) => onUpdate(idx, { chart_type: v })}>
                <SelectTrigger className="h-8 text-xs" data-testid={`series-chart-type-${idx}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CHART_TYPES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      <div className="flex items-center gap-2"><c.Icon className="h-3.5 w-3.5" /> {c.label}</div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Début</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="w-full h-8 justify-start text-xs font-normal" data-testid={`series-start-${idx}`}>
                    <CalendarIcon className="h-3 w-3 mr-1.5" />
                    {format(startDate, "dd/MM/yy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={startDate} onSelect={(d) => d && onUpdate(idx, { start_date: d })} locale={fr} />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="text-xs">Fin</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="w-full h-8 justify-start text-xs font-normal" data-testid={`series-end-${idx}`}>
                    <CalendarIcon className="h-3 w-3 mr-1.5" />
                    {format(endDate, "dd/MM/yy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={endDate} onSelect={(d) => d && onUpdate(idx, { end_date: d })} locale={fr} />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div>
              <Label className="text-xs">Vendeur(s)</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="w-full h-8 justify-between text-xs font-normal" data-testid={`series-sellers-${idx}`}>
                    <span className="truncate">
                      {selectedSellerNames.length === 0 ? "Tous"
                        : selectedSellerNames.length === 1 ? selectedSellerNames[0]
                        : `${selectedSellerNames.length} vendeurs`}
                    </span>
                    <ChevronDown className="h-3 w-3 opacity-50 ml-1" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-2 max-h-72 overflow-y-auto">
                  <div className="space-y-1.5">
                    {sellersList.map((sl) => (
                      <label key={sl.id} className="flex items-center gap-2 cursor-pointer hover:bg-accent rounded px-2 py-1">
                        <Checkbox
                          checked={s.seller_ids.includes(sl.id)}
                          onCheckedChange={() => onUpdate(idx, { seller_ids: toggleId(s.seller_ids, sl.id) })}
                        />
                        <span className="text-sm flex-1">{sl.name}</span>
                        {sl.archived && <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-200 text-zinc-600">archivé</span>}
                        {sl.virtual && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">virtuel</span>}
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="text-xs">Type(s) de commande</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="w-full h-8 justify-between text-xs font-normal" data-testid={`series-types-${idx}`}>
                    <span className="truncate">
                      {selectedTypeNames.length === 0 ? "Tous"
                        : selectedTypeNames.length === 1 ? selectedTypeNames[0]
                        : `${selectedTypeNames.length} types`}
                    </span>
                    <ChevronDown className="h-3 w-3 opacity-50 ml-1" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-2 max-h-72 overflow-y-auto">
                  <div className="space-y-1.5">
                    {orderTypes.map((t) => (
                      <label key={t.id} className="flex items-center gap-2 cursor-pointer hover:bg-accent rounded px-2 py-1">
                        <Checkbox
                          checked={s.order_type_ids.includes(t.id)}
                          onCheckedChange={() => onUpdate(idx, { order_type_ids: toggleId(s.order_type_ids, t.id) })}
                        />
                        <span className="text-sm flex-1">{t.name}</span>
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Switch
                  checked={!!s.cumulative}
                  onCheckedChange={(v) => onUpdate(idx, { cumulative: v })}
                  disabled={s.metric === "stock"}
                  data-testid={`series-cumul-${idx}`}
                />
                <Label className="text-xs">Cumulé</Label>
              </div>
              <input
                type="color"
                value={s.color}
                onChange={(e) => onUpdate(idx, { color: e.target.value })}
                className="w-8 h-8 rounded cursor-pointer border"
                title="Couleur"
                data-testid={`series-color-${idx}`}
              />
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

/**
 * Composant de comparaison avancée multi-séries.
 * Props :
 *  - orderTypes, sellersList : référentiel
 *  - persistKey : clé pour sauvegarder via user-table-prefs
 *  - height (number) : hauteur du graphique
 *  - compact (bool) : version dashboard
 */
export default function AdvancedCompare({
  orderTypes = [],
  sellersList = [],
  persistKey,
  height = 420,
  compact = false,
}) {
  const [seriesState, setSeriesState] = useState(() => [defaultSeries(0), defaultSeries(1)]);
  const [computed, setComputed] = useState([]);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [hasFetched, setHasFetched] = useState(false);
  const [viewOnly, setViewOnly] = useState(false);

  // ---- Load persisted config ----
  useEffect(() => {
    if (!persistKey) return;
    let cancelled = false;
    api.getUserTablePref(persistKey).then(({ data }) => {
      if (cancelled) return;
      const cfg = data?.sort?.config;
      if (Array.isArray(cfg) && cfg.length > 0) {
        const restored = cfg.map((s) => ({
          ...s,
          start_date: s.start_date ? new Date(s.start_date) : yearStart(today().getFullYear()),
          end_date: s.end_date ? new Date(s.end_date) : today(),
          order_type_ids: s.order_type_ids || [],
          seller_ids: s.seller_ids || [],
        }));
        setSeriesState(restored);
      }
      if (typeof data?.sort?.view_only === "boolean") {
        setViewOnly(data.sort.view_only);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [persistKey]);

  // ---- Auto-fetch once when persisted config loads (so the chart appears immediately in view-only mode) ----
  const [autoRanOnce, setAutoRanOnce] = useState(false);

  const persistConfig = useCallback(async (next, opts = {}) => {
    if (!persistKey) return;
    try {
      const cfg = next.map((s) => ({
        ...s,
        start_date: s.start_date instanceof Date ? s.start_date.toISOString() : s.start_date,
        end_date: s.end_date instanceof Date ? s.end_date.toISOString() : s.end_date,
      }));
      await api.upsertUserTablePref(persistKey, {
        sort: { config: cfg, view_only: typeof opts.viewOnly === "boolean" ? opts.viewOnly : viewOnly },
      });
    } catch (_e) { /* silent */ }
  }, [persistKey, viewOnly]);

  const toggleViewOnly = useCallback(() => {
    setViewOnly((prev) => {
      const next = !prev;
      // Persist asynchronously
      if (persistKey) {
        api.upsertUserTablePref(persistKey, {
          sort: {
            config: seriesState.map((s) => ({
              ...s,
              start_date: s.start_date instanceof Date ? s.start_date.toISOString() : s.start_date,
              end_date: s.end_date instanceof Date ? s.end_date.toISOString() : s.end_date,
            })),
            view_only: next,
          },
        }).catch(() => {});
      }
      return next;
    });
  }, [persistKey, seriesState]);

  const updateSeries = useCallback((idx, patch) => {
    setSeriesState((prev) => {
      const next = prev.map((s, i) => (i === idx ? { ...s, ...patch } : s));
      persistConfig(next);
      return next;
    });
  }, [persistConfig]);

  const addSeries = useCallback(() => {
    setSeriesState((prev) => {
      const next = [...prev, defaultSeries(prev.length)];
      persistConfig(next);
      return next;
    });
  }, [persistConfig]);

  const duplicateSeries = useCallback((idx) => {
    setSeriesState((prev) => {
      const src = prev[idx];
      const copy = {
        ...src,
        id: `s_${Date.now()}_${prev.length}`,
        label: `${src.label} (copie)`,
        color: colorFor(prev.length),
      };
      const next = [...prev, copy];
      persistConfig(next);
      return next;
    });
  }, [persistConfig]);

  const removeSeries = useCallback((idx) => {
    setSeriesState((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      persistConfig(next);
      return next;
    });
  }, [persistConfig]);

  const handleToggleOpen = useCallback((sid) => {
    setCollapsed((c) => ({ ...c, [sid]: !c[sid] }));
  }, []);

  const runCompare = useCallback(async () => {
    if (seriesState.length === 0) return;
    setLoading(true);
    try {
      const payload = {
        series: seriesState.map((s) => ({
          id: s.id,
          label: s.label,
          metric: s.metric,
          start_date: (s.start_date instanceof Date ? s.start_date : new Date(s.start_date)).toISOString(),
          end_date: (s.end_date instanceof Date ? s.end_date : new Date(s.end_date)).toISOString(),
          cumulative: !!s.cumulative,
          chart_type: s.chart_type,
          color: s.color,
          order_type_ids: s.order_type_ids?.length ? s.order_type_ids : null,
          seller_ids: s.seller_ids?.length ? s.seller_ids : null,
        })),
      };
      const res = await api.postAnalyticsAdvancedCompare(payload);
      setComputed(res.data.series || []);
      setHasFetched(true);
    } catch (_e) {
      toast.error("Erreur lors de la comparaison");
    } finally {
      setLoading(false);
    }
  }, [seriesState]);

  // Auto-run once after persisted config has loaded
  useEffect(() => {
    if (autoRanOnce) return;
    if (!persistKey) return;
    if (!seriesState.length) return;
    // Defer to next tick so all state from getUserTablePref has settled
    const t = setTimeout(() => {
      runCompare();
      setAutoRanOnce(true);
    }, 300);
    return () => clearTimeout(t);
  }, [persistKey, seriesState.length, autoRanOnce, runCompare]);

  const nonPieSeries = useMemo(() => computed.filter((s) => s.chart_type !== "pie"), [computed]);
  const pieSeries = useMemo(() => computed.filter((s) => s.chart_type === "pie"), [computed]);

  const chartData = useMemo(() => {
    const maxLen = Math.max(0, ...nonPieSeries.map((s) => s.points.length));
    const out = [];
    for (let i = 0; i < maxLen; i++) {
      const row = { __idx: i, __label: nonPieSeries[0]?.points[i]?.period_label?.split(" ")[0] || `M${i + 1}` };
      nonPieSeries.forEach((s) => {
        const p = s.points[i];
        if (p) {
          row[s.id] = p.value;
          row[`${s.id}__count`] = p.count;
          row[`${s.id}__period`] = p.period_label;
          row[`${s.id}__raw`] = p.raw_value;
        }
      });
      out.push(row);
    }
    return out;
  }, [nonPieSeries]);

  const hasCountSeries = nonPieSeries.some((s) => isCountMetric(s.metric));

  return (
    <Card data-testid="advanced-compare-root">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="font-heading flex items-center gap-2 text-base sm:text-lg">
            <GitCompare className="h-5 w-5 text-primary" />
            Comparaison avancée
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant={viewOnly ? "default" : "outline"}
              onClick={toggleViewOnly}
              data-testid="advanced-compare-toggle-viewonly"
              title={viewOnly ? "Afficher la configuration" : "Masquer la configuration"}
            >
              {viewOnly ? <Eye className="h-3.5 w-3.5 mr-1" /> : <EyeOff className="h-3.5 w-3.5 mr-1" />}
              {viewOnly ? "Éditer" : "Vue sobre"}
            </Button>
            {!viewOnly && (
              <Button size="sm" variant="outline" onClick={addSeries} data-testid="advanced-compare-add">
                <Plus className="h-3.5 w-3.5 mr-1" /> Ajouter une série
              </Button>
            )}
            <Button
              size="sm"
              onClick={runCompare}
              disabled={loading || seriesState.length === 0}
              data-testid="advanced-compare-run"
            >
              {loading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
              Lancer
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {!viewOnly && (
          <div className="space-y-2">
            {seriesState.map((s, i) => (
              <SeriesCard
                key={s.id}
                s={s}
                idx={i}
                totalCount={seriesState.length}
                orderTypes={orderTypes}
                sellersList={sellersList}
                open={!collapsed[s.id]}
                onToggleOpen={handleToggleOpen}
                onUpdate={updateSeries}
                onDuplicate={duplicateSeries}
                onRemove={removeSeries}
              />
            ))}
          </div>
        )}

        {hasFetched && nonPieSeries.length > 0 && chartData.length > 0 && (
          <ResponsiveContainer width="100%" height={height}>
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="__label" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="left" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k€`} tick={{ fontSize: 11 }} />
              {hasCountSeries && (
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} allowDecimals={false} />
              )}
              <Tooltip content={<CompareTooltip computed={computed} />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {nonPieSeries.map((s) => {
                const axis = isCountMetric(s.metric) ? "right" : "left";
                const color = s.color || "#1d4ed8";
                if (s.chart_type === "bar") {
                  return (
                    <Bar key={s.id} yAxisId={axis} dataKey={s.id} name={s.label}
                      fill={color} radius={[3, 3, 0, 0]} />
                  );
                }
                if (s.chart_type === "area") {
                  return (
                    <Area key={s.id} yAxisId={axis} type="monotone" dataKey={s.id} name={s.label}
                      stroke={color} fill={color} fillOpacity={0.25} strokeWidth={2} />
                  );
                }
                return (
                  <Line key={s.id} yAxisId={axis} type="monotone" dataKey={s.id} name={s.label}
                    stroke={color} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls={false} />
                );
              })}
            </ComposedChart>
          </ResponsiveContainer>
        )}

        {hasFetched && pieSeries.length > 0 && (
          <div className={`grid grid-cols-1 ${pieSeries.length > 1 ? "md:grid-cols-2" : ""} gap-4`}>
            {pieSeries.map((s) => {
              const pieData = s.points
                .filter((p) => (p.raw_value || 0) > 0)
                .map((p, i) => ({
                  name: p.period_label,
                  value: p.raw_value || 0,
                  fill: SERIES_COLORS[i % SERIES_COLORS.length],
                }));
              return (
                <Card key={s.id} className="border-l-4" style={{ borderLeftColor: s.color }} data-testid={`pie-card-${s.id}`}>
                  <CardHeader className="pb-1">
                    <CardTitle className="text-sm font-heading">
                      {s.label} · {isCountMetric(s.metric) ? `${Math.round(s.total)} cmd.` : formatCurrency(s.total)}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <ResponsiveContainer width="100%" height={compact ? 220 : 280}>
                      <PieChart>
                        <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                          outerRadius={compact ? 70 : 90} label={(d) => d.name} labelLine={false}>
                          {pieData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                        </Pie>
                        <Tooltip formatter={(v) => isCountMetric(s.metric) ? `${v} cmd.` : formatCurrency(v)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {!hasFetched && (
          <div className="text-center py-10 text-muted-foreground text-sm">
            Configure tes séries (métrique, période, vendeurs, type de graphique) puis clique sur « Lancer ».
          </div>
        )}

        {hasFetched && computed.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
            {computed.map((s) => (
              <div key={s.id} className="flex items-center gap-2 text-xs p-2 rounded bg-zinc-50" data-testid={`series-summary-${s.id}`}>
                <div className="w-3 h-3 rounded-full" style={{ background: s.color || "#1d4ed8" }} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{s.label}</p>
                  <p className="text-muted-foreground">
                    {isCountMetric(s.metric) ? `${Math.round(s.total)} cmd.` : formatCurrency(s.total)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
